import Chip from '@mui/material/Chip';
import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { JSX, ReactNode } from 'react';
import { useMemo } from 'react';

import type { PreviewList } from '~/analytics/events';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import type { TicketExpandableRowProps } from '~/components/table/expandableRow';
import { TICKET_DETAIL_PANEL_HEIGHT, TicketExpandableRow } from '~/components/table/expandableRow';
import { TicketPreviewPanel } from '~/components/tickets/TicketPreviewPanel';
import { getTicketsTableColumns } from '~/components/tickets/ticketTableColumns';
import type { TicketWithPortfolio } from '~/data/schemas/ticket';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';

const NO_ACTIONS: JSX.Element[] = [];

interface RelatedTicketsTableProps {
  previewList: PreviewList;
  tickets: TicketWithPortfolio[];
  isLoading: boolean;
  tableHeaderTitle: string;
  /** Per-row actions (e.g. Unlink). Omit for a purely informational table. */
  rowActions?: (ticket: TicketWithPortfolio) => JSX.Element[];
  /** A label shown beside a row's ticket number. */
  getRowLabel?: (ticket: TicketWithPortfolio) => string | undefined;
  /** The add control, rendered inside the table above its toolbar. */
  headerContent?: ReactNode;
  /** Server-side total. Omit for client-side paging over `tickets`. */
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  paginationMode?: 'client' | 'server';
  /**
   * Sort and filter state, for a server-paginated caller. Required alongside
   * `paginationMode="server"`: the grid holds one page, so a sort or filter it ran
   * itself would reorder or narrow that page while the footer still counted the
   * whole list. Passing them switches both to server mode.
   */
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
}

/**
 * Ticket grid for the invoice detail page's Related sections. Shares
 * `getTicketsTableColumns` and the expandable row with the main Tickets table, but
 * deliberately not `TicketTable` itself: that owns edit/delete dialogs, a CSV exporter
 * and persisted column preferences, none of which belong on an invoice's context
 * tables.
 */
export function RelatedTicketsTable({
  previewList,
  tickets,
  isLoading,
  tableHeaderTitle,
  rowActions,
  getRowLabel,
  headerContent,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  paginationMode = 'client',
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
}: RelatedTicketsTableProps) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const preview = useTableRowPreview({ list: previewList, apiRef });
  const terms = useLocaleTerms();
  const serverMode = paginationMode === 'server';

  const columns = useMemo(() => {
    const cols = getTicketsTableColumns({
      linkOnProperty: true,
      // `canEdit` is what appends the actions column; there is nothing to show
      // in it unless the caller passed row actions.
      canEdit: rowActions !== undefined,
      getActionsEl: rowActions ?? (() => NO_ACTIONS),
      canFilterProperty: false,
      terms,
    });
    if (!getRowLabel) return cols;
    return cols.map((col) =>
      col.field === 'number'
        ? {
            ...col,
            renderCell: ({ row }: { row: TicketWithPortfolio }) => {
              const label = getRowLabel(row);
              return (
                <>
                  {row.number}
                  {label ? (
                    <Chip label={label} size="small" variant="outlined" sx={{ ml: 1 }} />
                  ) : null}
                </>
              );
            },
          }
        : col,
    );
  }, [rowActions, getRowLabel, terms]);

  return (
    <>
      <LiftAITable<TicketWithPortfolio>
        height={tableHeight}
        apiRef={apiRef}
        rows={tickets}
        columns={columns}
        rowCount={rowCount ?? tickets.length}
        loading={isLoading}
        tableHeaderTitle={tableHeaderTitle}
        headerContent={headerContent}
        paginationMode={paginationMode}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortingMode={serverMode ? 'server' : 'client'}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterMode={serverMode ? 'server' : 'client'}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        getRowId={(row) => row.id}
        // The same panel the Tickets page expands. Passing it is also what gives the grid its
        // expand column.
        getDetailPanelContent={({ row }: TicketExpandableRowProps<TicketWithPortfolio>) => (
          <TicketExpandableRow row={row} />
        )}
        getDetailPanelHeight={() => TICKET_DETAIL_PANEL_HEIGHT}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false,
              property: false,
              portfolio: false,
              callTime: false,
              finishedTime: false,
              timeOnSiteOvertime: false,
              travelTimeOvertime: false,
            },
          },
        }}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? <TicketPreviewPanel rowPreview={preview.rowPreview} /> : null}
    </>
  );
}
